<template>
    <svg v-if="g" class="disegno-svg" :viewBox="viewBox" preserveAspectRatio="xMidYMid meet" role="img"
        :aria-label="`Anteprima serramento ${l} x ${h} mm`">
        <defs>
            <clipPath :id="idClip">
                <path :d="g.path" />
            </clipPath>
        </defs>

        <!-- Vetro, telaio (tratto interno) e ritti/traversi, tutti ritagliati sul contorno -->
        <path :d="g.path" class="vetro" />
        <g :clip-path="`url(#${idClip})`">
            <path :d="g.path" class="telaio" :stroke-width="t * 2" />
            <rect v-for="x in ritti" :key="`r${x}`" :x="x - t * 0.7" y="0" :width="t * 1.4" :height="g.ht"
                class="telaio-pieno" />
            <rect v-for="y in traversi" :key="`t${y}`" x="0" :y="y - t * 0.55" :width="g.w" :height="t * 1.1"
                class="telaio-pieno" />
        </g>
        <path :d="g.path" class="contorno" :stroke-width="sw" />

        <!-- Quote -->
        <g v-if="!compatto" class="quote" :stroke-width="sw" :font-size="fs">
            <line :x1="0" :y1="g.ht + m * 0.45" :x2="g.w" :y2="g.ht + m * 0.45" />
            <line :x1="0" :y1="g.ht + m * 0.3" :x2="0" :y2="g.ht + m * 0.6" />
            <line :x1="g.w" :y1="g.ht + m * 0.3" :x2="g.w" :y2="g.ht + m * 0.6" />
            <text :x="g.w / 2" :y="g.ht + m * 0.95" text-anchor="middle" class="testo">{{ fmt(l) }}</text>

            <line :x1="-m * 0.45" :y1="g.ht - hv" :x2="-m * 0.45" :y2="g.ht" />
            <line :x1="-m * 0.6" :y1="g.ht - hv" :x2="-m * 0.3" :y2="g.ht - hv" />
            <line :x1="-m * 0.6" :y1="g.ht" :x2="-m * 0.3" :y2="g.ht" />
            <text :x="-m * 0.7" :y="g.ht - hv / 2" text-anchor="middle" class="testo"
                :transform="`rotate(-90 ${-m * 0.7} ${g.ht - hv / 2})`">{{ fmt(h) }}</text>

            <template v-if="g.corda">
                <line :x1="g.w + m * 0.45" y1="0" :x2="g.w + m * 0.45" :y2="g.corda" />
                <text :x="g.w + m * 0.75" :y="g.corda / 2" text-anchor="middle" class="testo"
                    :transform="`rotate(-90 ${g.w + m * 0.75} ${g.corda / 2})`">C {{ fmt(corda) }}</text>
            </template>
        </g>
    </svg>
    <div v-else class="disegno-vuoto text-caption text-medium-emphasis">
        <v-icon :size="compatto ? 18 : 40" color="grey">mdi-image-off-outline</v-icon>
        <span v-if="!compatto" class="mt-2">Inserisci larghezza e altezza per vedere l'anteprima</span>
    </div>
</template>

<script>
import { geometria } from "@/utils/disegnoSerramento.js";

let contatore = 0;

// Anteprima schematica di sola lettura generata da forma, misure, corda, battenti e riempimenti.
// Non è il disegno CAD: serve a riconoscere a colpo d'occhio la posizione.
export default {
    name: "DisegnoSerramentoSvg",

    props: {
        forma: { type: String, default: "" },
        l: { type: [Number, String], default: null },
        h: { type: [Number, String], default: null },
        corda: { type: [Number, String], default: null },
        battenti: { type: [Number, String], default: 1 },
        nrRiempimenti: { type: [Number, String], default: 1 },
        // Versione miniatura (griglia): senza quote.
        compatto: { type: Boolean, default: false },
    },

    data() {
        contatore += 1;
        return { idClip: `disegno-clip-${contatore}` };
    },

    computed: {
        g() {
            return geometria({ forma: this.forma, l: this.l, h: this.h, corda: this.corda });
        },
        base() {
            return this.g ? Math.max(this.g.w, this.g.ht) : 1;
        },
        // Spessore del profilo, margine per le quote, tratto e corpo del testo, tutti proporzionali alla misura maggiore.
        t() {
            return this.g ? Math.min(this.g.w, this.g.ht) * 0.055 : 0;
        },
        m() {
            return this.base * 0.16;
        },
        sw() {
            return this.base * (this.compatto ? 0.012 : 0.004);
        },
        fs() {
            return this.base * 0.05;
        },
        // Altezza da quotare: la misura H inserita (spalla per centinato/casetta, lato sinistro per i trapezi).
        hv() {
            return this.g ? Math.min(Number(this.h) || this.g.ht, this.g.ht) : 0;
        },
        viewBox() {
            if (!this.g) return "0 0 1 1";
            if (this.compatto) {
                const p = this.base * 0.04;
                return `${-p} ${-p} ${this.g.w + 2 * p} ${this.g.ht + 2 * p}`;
            }
            const m = this.m;
            return `${-m * 1.1} ${-m * 0.2} ${this.g.w + m * 2.2} ${this.g.ht + m * 1.45}`;
        },
        ritti() {
            const n = Math.max(1, Math.round(Number(this.battenti) || 1));
            if (!this.g || this.g.tipo === "oblo") return [];
            return Array.from({ length: n - 1 }, (_, i) => (this.g.w * (i + 1)) / n);
        },
        traversi() {
            if (!this.g || this.g.tipo === "oblo") return [];
            const nr = Number(this.nrRiempimenti) || 1;
            if (nr === 2) return [this.g.ht * 0.68];
            if (nr === 3) return [this.g.ht * 0.36, this.g.ht * 0.68];
            return [];
        },
    },

    methods: {
        fmt(v) {
            return `${Math.round(Number(v) * 10) / 10} mm`;
        },
    },
};
</script>

<style scoped>
.disegno-svg {
    width: 100%;
    height: 100%;
    max-height: 100%;
    display: block;
}

.vetro {
    fill: #cfe6f3;
}

.telaio {
    fill: none;
    stroke: #b88a4a;
}

.telaio-pieno {
    fill: #b88a4a;
}

.contorno {
    fill: none;
    stroke: #6b4a1f;
    stroke-linejoin: round;
}

.quote line {
    stroke: currentColor;
}

.testo {
    fill: currentColor;
    stroke: none;
}

.disegno-vuoto {
    width: 100%;
    height: 100%;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    text-align: center;
}
</style>
